import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './login.module.css'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/reset-password')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: ResetPasswordPage,
})

interface FormErrors {
  password?: string
  confirmPassword?: string
}

function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setApiError(null)

    if (!validateForm()) return

    if (!token) {
      setApiError('No reset token provided. Please use the link from your email.')
      return
    }

    setIsLoading(true)

    api
      .resetPassword({ token, password })
      .then(() => {
        setSuccess(true)
      })
      .catch((err: unknown) => {
        setApiError(
          err instanceof Error ? err.message : 'Password reset failed. The token may be expired.'
        )
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (!token) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>🔍</span>
              <span className={styles.logoText}>PriceSpy</span>
            </div>
            <h2 className={styles.title}>Invalid Link</h2>
            <Alert variant="danger" className={styles.alert}>
              This password reset link is invalid or has expired. Please request a new one.
            </Alert>
            <Link to="/forgot-password">
              <Button variant="primary" className={styles.fullWidth}>
                Request New Link
              </Button>
            </Link>
          </CardBody>
        </Card>
      </main>
    )
  }

  if (success) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>🔍</span>
              <span className={styles.logoText}>PriceSpy</span>
            </div>
            <h2 className={styles.title}>Password Reset</h2>
            <Alert variant="success" className={styles.alert}>
              Your password has been successfully reset. You can now log in with your new password.
            </Alert>
            <Link to="/login">
              <Button variant="primary" className={styles.fullWidth}>
                Go to Login
              </Button>
            </Link>
          </CardBody>
        </Card>
      </main>
    )
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>🔍</span>
            <span className={styles.logoText}>PriceSpy</span>
          </div>
          <h2 className={styles.title}>Reset Your Password</h2>
          <p className={styles.subtitle}>Enter your new password below.</p>

          {apiError && (
            <Alert variant="danger" className={styles.alert}>
              {apiError}
            </Alert>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              {...(errors.password && { error: errors.password })}
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              {...(errors.confirmPassword && { error: errors.confirmPassword })}
              required
            />

            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                className={styles.fullWidth}
              >
                Reset Password
              </Button>
            </div>
          </form>

          <p className={styles.footer}>
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </CardBody>
      </Card>
    </main>
  )
}
